import { RecordType } from "@pilatestime/shared-types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useApiParameters } from "@/hooks/useApiParameters";
import {
	NewAnamnesisRecord,
	NewOtherRecord,
	NewPeriodicEvaluationRecord,
	NewPhysicalEvaluationRecord,
	NewTreatmentRecord,
} from "./-components";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customers/$customerId/medicalRecords/new",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		return {
			userId,
			token,
			orgId: params.orgId,
			customerId: params.customerId,
		};
	},
	component: NewMedicalRecordPage,
});

function NewMedicalRecordPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { orgId, customerId } = Route.useParams();
	const { token } = Route.useLoaderData();

	const [recordType, setRecordType] = useState<RecordType | "">("");

	const handleBackToRecords = () => {
		navigate({
			to: "/app/org/$orgId/customers/$customerId/medicalRecords",
			params: { orgId, customerId },
		});
	};

	const renderRecordForm = () => {
		const commonProps = {
			token,
			orgId,
			customerId,
			onSuccess: handleBackToRecords,
			onCancel: handleBackToRecords,
		};

		switch (recordType) {
			case RecordType.ANAMNESIS:
				return <NewAnamnesisRecord {...commonProps} />;
			case RecordType.PHYSICAL_EVALUATION:
				return <NewPhysicalEvaluationRecord {...commonProps} />;
			case RecordType.PERIODIC_EVALUATION:
				return <NewPeriodicEvaluationRecord {...commonProps} />;
			case RecordType.TREATMENT:
				return <NewTreatmentRecord {...commonProps} />;
			case RecordType.OTHER:
				return <NewOtherRecord {...commonProps} />;
			default:
				return null;
		}
	};

	return (
		<div className="flex flex-col space-y-6 pb-6">
			{/* Header */}
			<PageHeader
				title={t("medicalRecords.newRecord.title")}
				description={t("medicalRecords.newRecord.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
			/>

			{/* Record Type Selection */}
			<Card className="border-0 shadow-lg">
				<CardHeader className="bg-gradient-to-r from-accent/5 to-accent/10 border-b border-accent/10">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-accent/10 rounded-lg">
							<FileText className="h-5 w-5 text-accent" />
						</div>
						<div>
							<CardTitle className="text-base sm:text-lg">
								{t("medicalRecords.newRecord.selectType")}
							</CardTitle>
							<CardDescription className="text-xs sm:text-sm">
								{t("medicalRecords.newRecord.selectTypeDescription")}
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className="p-6">
					<div className="max-w-md">
						<Label htmlFor="recordType" className="mb-2 block">
							{t("medicalRecords.fields.recordType")} *
						</Label>
						<Select
							value={recordType}
							onValueChange={(value) => setRecordType(value as RecordType)}
						>
							<SelectTrigger id="recordType">
								<SelectValue
									placeholder={t("medicalRecords.selectRecordType")}
								/>
							</SelectTrigger>
							<SelectContent>
								{Object.values(RecordType).map((type) => (
									<SelectItem key={type} value={type}>
										{t(`medicalRecords.recordTypeLabels.${type}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</CardContent>
			</Card>

			{/* Type-specific Form */}
			{recordType && renderRecordForm()}
		</div>
	);
}
